(() => {
  const PICKER_SELECTOR =
    'variant-selects[data-jb-dependent-options="true"]';

  /*
   * Prevent the script from being registered twice if the same
   * product picker appears more than once on the page.
   */
  if (window.JenniBagDependentOptions?.initAll) {
    window.JenniBagDependentOptions.initAll(document);
    return;
  }

  const normalize = (value) =>
    String(value ?? '').trim().toLowerCase();

  const handleize = (value) =>
    normalize(value)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  /*
   * The product options are rendered as direct children of
   * <variant-selects>. This returns Model, Series, and Color
   * in their real Shopify order.
   */
  function getOptionGroups(picker) {
    return Array.from(picker.children).filter((element) => {
      return element.matches?.('[data-option-name]');
    });
  }

  function getSelectedValue(group) {
    if (!group) return '';

    const select = group.querySelector('select');

    if (select) {
      return select.value;
    }

    const checkedInput = group.querySelector(
      'input[type="radio"]:checked'
    );

    return checkedInput?.value || '';
  }

  /*
   * In your theme, each radio input is followed directly by its label.
   */
  function getInputLabel(input) {
    const nextElement = input.nextElementSibling;

    if (
      nextElement?.tagName === 'LABEL' &&
      nextElement.htmlFor === input.id
    ) {
      return nextElement;
    }

    return Array.from(
      input.parentElement?.querySelectorAll('label') || []
    ).find((label) => label.htmlFor === input.id);
  }

  /*
   * Show or hide a radio swatch.
   *
   * Existing but sold-out variants remain visible and receive the
   * theme's normal disabled styling.
   *
   * Combinations that do not exist are completely hidden.
   */
  function updateRadioState(input, exists, available) {
    const label = getInputLabel(input);

    input.hidden = !exists;
    input.style.display = exists ? '' : 'none';
    input.disabled = !exists;

    if (exists) {
      input.removeAttribute('aria-hidden');
    } else {
      input.setAttribute('aria-hidden', 'true');
    }

    if (label) {
      label.hidden = !exists;
      label.style.display = exists ? '' : 'none';
    }

    if (!exists) return;

    /*
     * Refresh the theme's sold-out classes based on the currently
     * selected Model and Series.
     */
    input.classList.toggle('disabled', !available);

    if (input.classList.contains('swatch-input__input')) {
      input.classList.toggle('visually-disabled', !available);
    }
  }

  /*
   * Basic support if the picker is changed to dropdown style later.
   */
  function updateDropdownOptionState(option, exists, available) {
    option.hidden = !exists;
    option.disabled = !exists;

    if (exists) {
      option.classList.toggle('dropdown_disabled', !available);
    }
  }

  function updateColorLabel(colorGroup, selectedControl) {
    if (!colorGroup || !selectedControl) return;

    const selectedValue = selectedControl.value;

    const selectedValueLabel = colorGroup.querySelector(
      '.dbtfy-product-option-modal-label [data-selected-value]'
    );

    if (selectedValueLabel) {
      selectedValueLabel.textContent = selectedValue;
    }

    /*
     * Update the small dropdown swatch too, in case the theme is ever
     * switched from swatches to swatch-dropdown style.
     */
    if (selectedControl.tagName !== 'OPTION') return;

    const dropdownSwatch =
      colorGroup.querySelector('.dropdown-swatch');

    if (!dropdownSwatch) return;

    dropdownSwatch.dataset.selectedValue =
      handleize(selectedValue);

    const swatchElement =
      dropdownSwatch.querySelector('.swatch');

    if (!swatchElement) return;

    const swatchValue =
      selectedControl.dataset.optionSwatchValue;

    if (swatchValue) {
      swatchElement.style.setProperty(
        '--swatch--background',
        swatchValue
      );

      swatchElement.classList.remove('swatch--unavailable');
      dropdownSwatch.classList.remove('empty-swatch');
    } else {
      swatchElement.style.setProperty(
        '--swatch--background',
        'unset'
      );

      swatchElement.classList.add('swatch--unavailable');
      dropdownSwatch.classList.add('empty-swatch');
    }

    swatchElement.style.setProperty(
      '--swatch-focal-point',
      selectedControl.dataset.optionSwatchFocalPoint || 'unset'
    );
  }

  /*
   * Select a replacement color without sending an extra change event.
   *
   * The original Model or Series change will continue to the theme's
   * VariantSelects controller, which will then read the corrected
   * complete combination.
   */
  function selectColorControl(
    colorGroup,
    selectedControl,
    allControls
  ) {
    if (!selectedControl) return;

    if (selectedControl.tagName === 'OPTION') {
      const select = selectedControl.parentElement;

      Array.from(select.options).forEach((option) => {
        const isSelected = option === selectedControl;

        option.selected = isSelected;

        if (isSelected) {
          option.setAttribute('selected', 'selected');
        } else {
          option.removeAttribute('selected');
        }
      });
    } else {
      allControls.forEach((input) => {
        input.checked = input === selectedControl;
      });
    }

    updateColorLabel(colorGroup, selectedControl);
  }

  function syncColorOptions(picker) {
    const state = picker.__jbDependentOptions;

    if (!state) return;

    const {
      variants,
      modelGroup,
      seriesGroup,
      colorGroup,
      modelIndex,
      seriesIndex,
      colorIndex,
    } = state;

    const selectedModel = getSelectedValue(modelGroup);
    const selectedSeries = getSelectedValue(seriesGroup);

    if (!selectedModel || !selectedSeries) return;

    /*
     * Find variants matching the currently selected Model and Series.
     *
     * We do not filter by availability here because a sold-out but
     * real color should stay visible.
     */
    const matchingVariants = variants.filter((variant) => {
      if (!Array.isArray(variant.options)) return false;

      return (
        normalize(variant.options[modelIndex]) ===
          normalize(selectedModel) &&
        normalize(variant.options[seriesIndex]) ===
          normalize(selectedSeries)
      );
    });

    if (!matchingVariants.length) {
      console.warn(
        'JenniBag dependent options: no variants matched the selected Model and Series.',
        selectedModel,
        selectedSeries
      );

      return;
    }

    /*
     * Create a map like:
     *
     * beige => true
     * brown => true
     *
     * The boolean tells us whether at least one matching variant
     * for that color is currently purchasable.
     */
    const colors = new Map();

    matchingVariants.forEach((variant) => {
      const colorKey = normalize(
        variant.options[colorIndex]
      );

      const previousAvailability =
        colors.get(colorKey) || false;

      colors.set(
        colorKey,
        previousAvailability || Boolean(variant.available)
      );
    });

    const colorSelect = colorGroup.querySelector('select');

    const controls = colorSelect
      ? Array.from(colorSelect.options)
      : Array.from(
          colorGroup.querySelectorAll(
            'input[type="radio"]'
          )
        );

    /*
     * Hide colors that do not exist for this Model + Series.
     * Keep real sold-out colors visible.
     */
    controls.forEach((control) => {
      const colorKey = normalize(control.value);
      const exists = colors.has(colorKey);
      const available = colors.get(colorKey) === true;

      if (control.tagName === 'OPTION') {
        updateDropdownOptionState(
          control,
          exists,
          available
        );
      } else {
        updateRadioState(
          control,
          exists,
          available
        );
      }
    });

    const currentlySelected = colorSelect
      ? colorSelect.selectedOptions[0]
      : colorGroup.querySelector(
          'input[type="radio"]:checked'
        );

    /*
     * Keep the current color when it still belongs to the new Series.
     *
     * Example:
     * Original Beige → Signature keeps Beige.
     */
    if (
      currentlySelected &&
      colors.has(normalize(currentlySelected.value))
    ) {
      updateColorLabel(colorGroup, currentlySelected);
      return;
    }

    const validControls = controls.filter((control) => {
      return colors.has(normalize(control.value));
    });

    if (!validControls.length) return;

    const controlIsAvailable = (control) => {
      return colors.get(normalize(control.value)) === true;
    };

    const controlIsBeige = (control) => {
      return normalize(control.value) === 'beige';
    };

    /*
     * Replacement color priority:
     *
     * 1. Available Beige
     * 2. First available valid color
     * 3. Beige, even if sold out
     * 4. First valid color
     */
    const replacementColor =
      validControls.find(
        (control) =>
          controlIsBeige(control) &&
          controlIsAvailable(control)
      ) ||
      validControls.find(controlIsAvailable) ||
      validControls.find(controlIsBeige) ||
      validControls[0];

    selectColorControl(
      colorGroup,
      replacementColor,
      controls
    );
  }

  function initPicker(picker) {
    if (
      !picker ||
      picker.dataset.jbDependentInitialized === 'true'
    ) {
      return;
    }

    const variantsScript = picker.querySelector(
      'script[data-product-variants]'
    );

    if (!variantsScript) {
      console.warn(
        'JenniBag dependent options: product variant JSON was not found.'
      );

      return;
    }

    let variants;

    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      console.error(
        'JenniBag dependent options: variant JSON could not be read.',
        error
      );

      return;
    }

    const optionGroups = getOptionGroups(picker);

    const modelIndex = optionGroups.findIndex(
      (group) =>
        normalize(group.dataset.optionName) === 'model'
    );

    const seriesIndex = optionGroups.findIndex(
      (group) =>
        normalize(group.dataset.optionName) === 'series'
    );

    const colorIndex = optionGroups.findIndex(
      (group) =>
        normalize(group.dataset.optionName) === 'color'
    );

    if (
      modelIndex === -1 ||
      seriesIndex === -1 ||
      colorIndex === -1
    ) {
      console.warn(
        'JenniBag dependent options: Model, Series, or Color option was not found.'
      );

      return;
    }

    picker.__jbDependentOptions = {
      variants,
      modelGroup: optionGroups[modelIndex],
      seriesGroup: optionGroups[seriesIndex],
      colorGroup: optionGroups[colorIndex],
      modelIndex,
      seriesIndex,
      colorIndex,
    };

    picker.dataset.jbDependentInitialized = 'true';

    /*
     * Capture phase is important.
     *
     * It corrects the Color before global.js processes the customer's
     * Model or Series change.
     */
    picker.addEventListener(
      'change',
      (event) => {
        const changedGroup = event.target.closest?.(
          '[data-option-name]'
        );

        if (!changedGroup || !picker.contains(changedGroup)) {
          return;
        }

        const changedOption = normalize(
          changedGroup.dataset.optionName
        );

        if (
          changedOption === 'model' ||
          changedOption === 'series'
        ) {
          syncColorOptions(picker);
        }
      },
      true
    );

    /*
     * Filter the colors immediately when the page first loads.
     */
    syncColorOptions(picker);
  }

  function initAll(root = document) {
    if (root.matches?.(PICKER_SELECTOR)) {
      initPicker(root);
    }

    root
      .querySelectorAll?.(PICKER_SELECTOR)
      .forEach(initPicker);
  }

  window.JenniBagDependentOptions = {
    initAll,
  };

  /*
   * Initialize immediately for the normal product page.
   */
  initAll(document);

  /*
   * Also initialize after Shopify or Debutify dynamically inserts
   * another copy of the picker.
   */
  document.addEventListener(
    'variant-selects:loaded',
    (event) => {
      initAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(event.target);
    }
  );

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initAll(document);
    },
    { once: true }
  );
})();